import moment from "moment";
import {
  IRoomPricePerDay,
  IRoomPricePerDayPriceItem,
  IRoomPricingPeriod,
} from "../../common/types";
import { applyHotelBirthdayPromo } from "../../common/util/hotelBirthdayPromo";

export const calculateRefundableDate = (
  checkInDate: moment.Moment,
  refundableDays: number | null | undefined,
) => {
  if (refundableDays == null) return null;
  const days = Number(refundableDays);
  return Number.isFinite(days) && days >= 0
    ? checkInDate.clone().subtract(days, "days")
    : null;
};

// Checks whether a price document covers a given date.
export const priceCoversDate = (price: IRoomPricePerDay, date: string) => {
  if (!price.dateRangeEnd) {
    return price.date === date;
  }

  return price.date <= date && date <= price.dateRangeEnd;
};

// Applies early booking discount for a period if it is still valid.
export const applyEarlyDiscount = (
  price: number,
  period?: IRoomPricingPeriod,
) => {
  if (!period?.earlyDiscountEndDate) return price;
  const percentage = Number(period.earlyDiscountPercentage);
  if (!Number.isFinite(percentage) || percentage <= 0) return price;
  const endDate = moment(period.earlyDiscountEndDate, "YYYY-MM-DD");
  const now = moment();
  const isBeforeEndDate =
    now.isBefore(endDate, "day") || now.isSame(endDate, "day");
  if (!isBeforeEndDate) return price;
  return price - (price * percentage) / 100;
};

// Filters price items by guest counts (adults/children/infants).
export const matchesGuests = (
  item: IRoomPricePerDayPriceItem,
  adults: number,
  children: number,
  infants: number,
) => {
  const matchesAdults = Number(item.adults ?? 0) === +adults;
  const matchesChildren = Number(item.childs ?? 0) === +children;
  const infantsValue = (item as any).infants;
  const matchesInfants =
    infantsValue == null || Number(infantsValue) >= +infants;
  return matchesAdults && matchesChildren && matchesInfants;
};

// Chooses the best matching price item for a partner, with sensible fallbacks.
export const selectPreferredPriceItem = (
  items: IRoomPricePerDayPriceItem[],
  normalizedPartnerId?: string,
) => {
  const preferredPartnerId = normalizedPartnerId || "GENERAL";
  return (
    items.find((item) => item.partnerId === preferredPartnerId) ||
    items.find(
      (item) =>
        normalizedPartnerId &&
        item.partnersDiscountPrice &&
        item.partnersDiscountPrice[normalizedPartnerId] != null,
    ) ||
    items.find((item) => item.partnerId === "GENERAL") ||
    items[0]
  );
};

// Creates a period map and flattened periods list from price documents.
export const buildPeriodMapFromPrices = (
  prices: IRoomPricePerDay[],
  periodsById: Record<string, IRoomPricingPeriod>,
) => {
  const periodMap = prices.reduce(
    (acc: Record<string, IRoomPricingPeriod>, price) => {
      if (price?.periodId && periodsById[price.periodId]) {
        acc[price.periodId] = periodsById[price.periodId];
      }
      return acc;
    },
    {},
  );
  const periods = Object.values(periodMap);
  return { periodMap, periods };
};

// Calculates the minimum stay for a room based on prices and periods.
export const computeMinStay = (
  prices: IRoomPricePerDay[],
  periodMap: Record<string, IRoomPricingPeriod>,
  periods: IRoomPricingPeriod[],
) => {
  const minStayCandidates = prices
    .map((price) => {
      const period = price?.periodId ? periodMap[price.periodId] : null;
      const candidate =
        Number(period?.minimumStay) ||
        Number(
          (price as any)?.stayNights || (period as any)?.minimumStayNights || 0,
        );
      return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
    })
    .filter(Boolean) as number[];
  const periodFallback = periods.reduce((max, period) => {
    const val = Number(period?.minimumStay || 0);
    return Number.isFinite(val) && val > max ? val : max;
  }, 0);
  return minStayCandidates.length > 0
    ? Math.max(...minStayCandidates)
    : periodFallback || 1;
};

// Computes availability flags and remaining rooms across the travel dates.
export const computeAvailabilityMeta = (
  prices: IRoomPricePerDay[],
  travelDates: string[],
) => {
  const relevantPrices = travelDates.length
    ? prices.filter((price) =>
        travelDates.some((date) => priceCoversDate(price, date)),
      )
    : prices;
  const allotments = relevantPrices.map((price) =>
    Number(price?.roomAllotment ?? 0),
  );
  const hasPositiveAllotment = allotments.some((a) => a > 0);
  const allZeroAllotmentFromAllotment =
    allotments.length > 0 && allotments.every((a) => a <= 0);
  const allPricesStopSale =
    relevantPrices.length > 0 &&
    relevantPrices.every((price) => price.stopSale);
  const anyStopSale = relevantPrices.some((price) => price.stopSale);
  const availableRoomsCount = travelDates.length
    ? travelDates.reduce((minAvailable: number, date: string) => {
        const matches = prices.filter(
          (price) =>
            priceCoversDate(price, date) &&
            !price.stopSale &&
            price.status !== false,
        );
        if (!matches.length) return 0;
        const maxAvailable = matches.reduce((max: number, match: any) => {
          const available = Math.max(
            0,
            (match.roomAllotment || 0) - (match.allotment || 0),
          );
          return available > max ? available : max;
        }, 0);
        return Math.min(minAvailable, maxAvailable);
      }, Number.POSITIVE_INFINITY)
    : 0;

  const isSoldOutForDates =
    travelDates.length > 0 && prices.length > 0 && availableRoomsCount <= 0;

  return {
    hasPositiveAllotment,
    allZeroAllotment: allZeroAllotmentFromAllotment || isSoldOutForDates,
    allPricesStopSale,
    anyStopSale,
    availableRoomsCount:
      availableRoomsCount === Number.POSITIVE_INFINITY
        ? 0
        : availableRoomsCount,
  };
};

// Calculates room totals across all dates, including discounts and selling price.
export const calculateRoomTotals = (params: {
  prices: IRoomPricePerDay[];
  travelDates: string[];
  periodsById: Record<string, IRoomPricingPeriod>;
  adults: number;
  children: number;
  infants: number;
  normalizedPartnerId?: string;
  isReleaseInFuture?: boolean;
  hotelId?: string;
}) => {
  const {
    prices,
    travelDates,
    periodsById,
    adults,
    children,
    infants,
    normalizedPartnerId,
    isReleaseInFuture,
    hotelId,
  } = params;
  let totalPrice = 0;
  let discountTotalPrice = 0;
  let sellingTotalPrice = 0;
  let netTotalPrice = 0;
  let isBookableForAllDates = true;
  let hasOnRequestPeriod = false;

  for (const date of travelDates) {
    const matchingDocs = prices.filter(
      (price) =>
        priceCoversDate(price, date) &&
        !price.stopSale &&
        price.status !== false,
      // +(price.roomAllotment || 0) >= +(price.allotment || 0),
    );

    if (!matchingDocs.length) {
      isBookableForAllDates = false;
      break;
    }

    let bestNightPrice = Number.POSITIVE_INFINITY;
    let bestNightDiscountPrice = Number.POSITIVE_INFINITY;
    let bestNightSellingPrice = Number.POSITIVE_INFINITY;
    let bestNightNetPrice = Number.POSITIVE_INFINITY;
    for (const priceDoc of matchingDocs) {
      const period =
        priceDoc.periodId && periodsById[priceDoc.periodId]
          ? periodsById[priceDoc.periodId]
          : undefined;
      if (period?.onRequest) {
        hasOnRequestPeriod = true;
      }
      const matchingItems = (priceDoc.priceList || []).filter((item) =>
        matchesGuests(item, adults, children, infants),
      );
      for (const item of matchingItems) {
        const hasPartnerPrice =
          normalizedPartnerId &&
          item.partnersDiscountPrice &&
          item.partnersDiscountPrice[normalizedPartnerId] != null;
        const rawPrice = hasPartnerPrice
          ? Number(item.partnersDiscountPrice?.[normalizedPartnerId])
          : Number(item.totalPrice);
        if (!Number.isFinite(rawPrice)) continue;
        const discountedPrice = applyEarlyDiscount(rawPrice, period);
        const promoDiscountedPrice = applyHotelBirthdayPromo({
          price: discountedPrice,
          partnerId: normalizedPartnerId?.toUpperCase(),
          hotelId,
        });
        if (promoDiscountedPrice < bestNightPrice) {
          bestNightPrice = promoDiscountedPrice;
        }

        const rawDiscountPrice = Number(item.discountPrice);
        if (
          Number.isFinite(rawDiscountPrice) &&
          rawDiscountPrice < bestNightDiscountPrice
        ) {
          bestNightDiscountPrice = rawDiscountPrice;
        }

        const rawSellingPrice = Number(item.totalPrice);
        const fallbackSellingPrice = Number(item.partnersDiscountPrice?.POUPAS);
        const sellingPrice = Number.isFinite(rawSellingPrice)
          ? rawSellingPrice
          : Number.isFinite(fallbackSellingPrice)
            ? fallbackSellingPrice
            : rawPrice;
        if (
          Number.isFinite(sellingPrice) &&
          sellingPrice < bestNightSellingPrice
        ) {
          bestNightSellingPrice = sellingPrice;
        }

        const rawNetPrice = Number((item as any).netPrice);
        const netPrice = Number.isFinite(rawNetPrice) ? rawNetPrice : rawPrice;
        if (Number.isFinite(netPrice) && netPrice < bestNightNetPrice) {
          bestNightNetPrice = netPrice;
        }
      }
    }

    if (!Number.isFinite(bestNightPrice)) {
      isBookableForAllDates = false;
      break;
    }

    totalPrice += bestNightPrice;
    if (Number.isFinite(bestNightDiscountPrice)) {
      discountTotalPrice += bestNightDiscountPrice;
    }
    if (Number.isFinite(bestNightSellingPrice)) {
      sellingTotalPrice += bestNightSellingPrice;
    }
    if (Number.isFinite(bestNightNetPrice)) {
      netTotalPrice += bestNightNetPrice;
    }
  }

  return {
    totalPrice,
    discountTotalPrice,
    sellingTotalPrice,
    netTotalPrice,
    isBookableForAllDates,
    hasOnRequestPeriod,
    isReleaseInFuture: !!isReleaseInFuture,
  };
};
